import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  HairlineCard,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type {
  ResolvedWorkflowDefinition,
  WorkflowField,
  WorkflowStep,
  WorkflowStepRecord,
  WorkflowStepState,
} from '@munifocus/contracts-gov';
import { pickLang, useSaveWorkflowStep } from '../../lib/bylaw-workflows-api';
import { WorkflowDocumentActions } from './WorkflowDocumentActions';

type Value = string | number | boolean | null;

/** What a field holds in the form: inputs edit text, so numbers travel as text. */
function toInput(field: WorkflowField, value: Value | undefined): string {
  if (value === null || value === undefined) return '';
  if (field.type === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

/** Back from form text to the typed value the API checks. Blank clears. */
function fromInput(field: WorkflowField, input: string): Value {
  if (input.trim() === '') return null;
  if (field.type === 'boolean') return input === 'yes';
  if (field.type === 'number') {
    const n = Number(input);
    return Number.isFinite(n) ? n : null;
  }
  return input;
}

function initialInputs(step: WorkflowStep, record: WorkflowStepRecord | undefined) {
  return Object.fromEntries(step.fields.map((f) => [f.key, toInput(f, record?.values?.[f.key])]));
}

/**
 * One step of a configured workflow, rendered from its definition: its fields
 * by type, its checklist with the statute each item answers to, and what is
 * still required. Saved on its own, so an officer can record a reinspection
 * date today and its outcome next week.
 */
export function WorkflowStepCard({
  fileId,
  definition,
  step,
  state,
  record,
  readOnly,
}: {
  fileId: string;
  definition: ResolvedWorkflowDefinition;
  step: WorkflowStep;
  state: WorkflowStepState | undefined;
  record: WorkflowStepRecord | undefined;
  readOnly: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const save = useSaveWorkflowStep(fileId);
  const [inputs, setInputs] = useState<Record<string, string>>(() => initialInputs(step, record));
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => ({
    ...(record?.checklist ?? {}),
  }));

  // A save, or another officer's save, replaces the record; follow it.
  const recordStamp = JSON.stringify(record ?? null);
  useEffect(() => {
    setInputs(initialInputs(step, record));
    setChecklist({ ...(record?.checklist ?? {}) });
  }, [recordStamp]);

  const locked = state?.locked ?? false;
  const disabled = readOnly || locked || save.isPending;
  const missing = new Set(state?.missing ?? []);
  const lockedBy = step.requires
    .map((key) => definition.steps.find((s) => s.key === key))
    .filter((s): s is WorkflowStep => !!s)
    .map((s) => pickLang(s.title, lang))
    .join(', ');

  const onSave = () => {
    const values: Record<string, Value> = {};
    for (const field of step.fields) values[field.key] = fromInput(field, inputs[field.key] ?? '');
    save.mutate({ stepKey: step.key, dto: { values, checklist } });
  };

  const label = (field: WorkflowField) =>
    `${pickLang(field.label, lang)}${field.required ? ' *' : ''}`;

  return (
    <HairlineCard sx={{ p: 2.5 }} data-testid={`workflow-step-${step.key}`}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" component="h2" sx={{ fontSize: 17 }}>
            {pickLang(step.title, lang)}
          </Typography>
          {state?.complete ? (
            <StatusChip status="success" label={t('gov.bylawWorkflow.stage.stepDone')} />
          ) : (
            <StatusChip
              status="neutral"
              label={t('gov.bylawWorkflow.stage.missing', { count: state?.missing.length ?? 0 })}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {pickLang(step.description, lang)}
        </Typography>

        {locked && (
          <Alert severity="info">{t('gov.bylawWorkflow.stage.locked', { steps: lockedBy })}</Alert>
        )}

        {step.fields.map((field) => {
          const value = inputs[field.key] ?? '';
          const set = (v: string) => setInputs((prev) => ({ ...prev, [field.key]: v }));
          const help = field.help ? pickLang(field.help, lang) : undefined;
          const error = !readOnly && !locked && missing.has(field.key) && value === '';
          if (field.type === 'select' || field.type === 'boolean') {
            const options =
              field.type === 'boolean'
                ? [
                    { value: 'yes', label: t('gov.bylawWorkflow.stage.yes') },
                    { value: 'no', label: t('gov.bylawWorkflow.stage.no') },
                  ]
                : (field.options ?? []).map((o) => ({
                    value: o.value,
                    label: pickLang(o.label, lang),
                  }));
            return (
              <FormControl key={field.key} fullWidth size="small" disabled={disabled}>
                <InputLabel>{label(field)}</InputLabel>
                <Select
                  label={label(field)}
                  aria-label={pickLang(field.label, lang)}
                  value={value}
                  onChange={(e) => set(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>{t('gov.bylawWorkflow.stage.notAnswered')}</em>
                  </MenuItem>
                  {options.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
                {help && <FormHelperText>{help}</FormHelperText>}
              </FormControl>
            );
          }
          return (
            <TextField
              key={field.key}
              fullWidth
              size="small"
              disabled={disabled}
              label={label(field)}
              value={value}
              error={error}
              helperText={help}
              onChange={(e) => set(e.target.value)}
              multiline={field.type === 'textarea'}
              minRows={field.type === 'textarea' ? 3 : undefined}
              type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
              slotProps={{
                inputLabel: field.type === 'date' ? { shrink: true } : undefined,
                htmlInput: { 'aria-label': pickLang(field.label, lang) },
              }}
            />
          );
        })}

        {step.checklist.length > 0 && (
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{t('gov.bylawWorkflow.stage.checklist')}</Typography>
            {step.checklist.map((item) => (
              <Stack key={item.key} spacing={0}>
                <FormControlLabel
                  disabled={disabled}
                  control={
                    <Checkbox
                      size="small"
                      checked={checklist[item.key] === true}
                      onChange={(e) =>
                        setChecklist((prev) => ({ ...prev, [item.key]: e.target.checked }))
                      }
                    />
                  }
                  label={`${pickLang(item.label, lang)}${item.required ? ' *' : ''}`}
                />
                {item.statuteKey && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                    {definition.citations[item.statuteKey] ||
                      t('gov.bylawWorkflow.citationNotConfigured')}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {step.documents.length > 0 && (
          <WorkflowDocumentActions
            fileId={fileId}
            templates={definition.documents.filter((d) => step.documents.includes(d.key))}
            disabled={readOnly}
          />
        )}

        {!readOnly && (
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={onSave} disabled={disabled}>
              {save.isPending
                ? t('gov.bylawWorkflow.stage.saving')
                : t('gov.bylawWorkflow.stage.save')}
            </Button>
          </Stack>
        )}
      </Stack>
    </HairlineCard>
  );
}
